import axios from "axios";
import React, { useCallback, useState, useEffect } from "react";
import { GENERATE_REPORT } from "../../../api/apiUrls";
import Select from "react-select";
import { packageCodes, stStatusOptions } from "../../../static/dropdown";
import DocumentTables from "./DocumentTables";
import { useSearchParams } from "react-router-dom";
import NotAuthorize from "../../../error/NotAuthorize";
import MenuIcon from "@mui/icons-material/Menu";
import {
  buildCondition,
  getActiveMonths,
  useMonthlyDateRanges,
} from "../../../static/hook";
// import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
// import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
// import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CustomSpinner from "../../../components/CustomSpinner";

const DocumentDashboard = ({ setShowSideOverlay, user }) => {
  const [spinnerLoading, setSpinnerLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const userCode = parseInt(searchParams.get("userCode"));
  const [allReportLoading, setAllReportLoading] = useState(false);
  const [cordinateLoading, setCordinateLoading] = useState(false);
  const [docLoading, setDocLoading] = useState(false);
  const [allDocData, setAllDocData] = useState(null);
  const [intakeDataLoading, setIntakeDataLoading] = useState(false);
  const [intakeData, setIntakeData] = useState(null);
  const [docRatioLoading, setDocRatioLoading] = useState(false);
  const [docRatioData, setDocRatioData] = useState(null);
  const [cordinatorDocLoading, setCordinatorDocLoading] = useState(false);
  const [cordinatorDocData, setCordinatorDocData] = useState(null);
  const [monthDataLoading, setMonthDataLoading] = useState(false);
  const [monthData, setMonthData] = useState(null);
  const [coordinatorData, setCoordinatorData] = useState(null);
  const [docWiseData, setDocWiseData] = useState(null);
  const [authState, setAuthState] = useState(false);
  const [yearState, setYearState] = useState(2025);
  const [dateRange, setDateRange] = useState([]);
  const [cordinatorList, setCordinatorList] = useState(null);
  const [counselorName, setCounseleorName] = useState("");
  const [dateFilterRange, setDateFilterRange] = useState([null, null]);

  const [filterDropdown, setFilterDropdown] = useState({
    intakeOptions: [], 
    locationOptions: [],
    checklistOptions: [],
    categoryOptions: [],
  });

  const [filterOptions, setFilterOptions] = useState({
    location: [],
    package: [],
    intake: [],
    checklist: [],
    course: [],
    mandatory: [],
    ststatus: [],
    category: [],
  });
  const rangeList = useMonthlyDateRanges(dateFilterRange);
  const handleChange = (index, value) => {
    const updatedRange = dateFilterRange;
    updatedRange[index] = value; // value will be in 'YYYY-MM-DD' format
    setDateFilterRange(updatedRange);
  };

  const fetchBaseQuery = useCallback(async (query) => {
    if (!query?.trim()) {
      console.warn("No query found to execute");
      return null;
    }
    try {
      const { data } = await axios.post(GENERATE_REPORT, { query });
      return data || null;
    } catch (error) {
      console.error(
        "Error occurred in fetchBaseQuery:",
        error?.response?.data?.message || error.message || "Unknown error"
      );
      return null;
    }
  }, []);

  const fetchFilterDropdown = useCallback(async () => {
    try {
      const queries = {
        intakeOptions:
          "SELECT IntakeCode AS 'Code', Intakename AS 'Name' FROM intakemas",
        locationOptions:
          "SELECT LocCode AS 'Code', locname AS 'Name' FROM locmas",
        checklistOptions:
          "SELECT DoctypeCode AS 'Code', DoctypeName AS 'Name' FROM DocTypeMas WHERE DoctypeCode NOT IN (11, 12, 13, 17, 26)",
        categoryOptions:
          "SELECT CatCode AS 'Code', CatName AS 'Name' FROM catmas",
      };

      const fetchPromises = Object.entries(queries).map(
        async ([key, query]) => {
          const data = await fetchBaseQuery(query);
          return { key, data };
        }
      );

      const results = await Promise.all(fetchPromises);

      const updatedDropdowns = results.reduce((acc, { key, data }) => {
        if (data?.length) {
          acc[key] = data.map((item) => ({
            label: item.NAME || "Unknown",
            value: item.CODE || "N/A",
          }));
        } else {
          console.warn(`No data received for ${key}`);
        }
        return acc;
      }, {});

      if (Object.keys(updatedDropdowns).length) {
        setFilterDropdown((prev) => ({ ...prev, ...updatedDropdowns }));
      }
    } catch (error) {
      console.error("Error fetching filter dropdown options:", error.message);
    }
  }, [fetchBaseQuery]);

  const fetchAllDocumentsReports = async () => {
    setAllReportLoading(true);
    try {
      const filters = [];
      const intakeCondition = buildCondition(
        "s.intakeCode",
        filterOptions.intake,
        true
      );
      const ststatusCondition = buildCondition(
        "s.ststatus",
        filterOptions.ststatus,
        true
      );
      const packageCondition = buildCondition(
        "s.packageCode",
        filterOptions.package,
        true
      );
      const locationCondition = buildCondition(
        "s.LocCode",
        filterOptions.location,
        true
      );
      const doctTypeCondition = buildCondition(
        "sd.DoctypeCode",
        filterOptions.checklist
      );
      const courseTypeCondition = buildCondition(
        "s.coursetype",
        filterOptions.course
      );
      const mandatoryTypeCondition = buildCondition(
        "d.mandatory",
        filterOptions.mandatory
      );
      const categoryTypeCondition = buildCondition(
        "s.catCode",
        filterOptions.category
      );

      [
        intakeCondition,
        ststatusCondition,
        packageCondition,
        locationCondition,
        doctTypeCondition,
        courseTypeCondition,
        mandatoryTypeCondition,
        categoryTypeCondition
      ].forEach((cond) => {
        if (cond) filters.push(cond);
      });

      const rangeCondition =
        dateFilterRange?.length === 2 &&
        typeof dateFilterRange[0] === "string" &&
        typeof dateFilterRange[1] === "string" &&
        dateFilterRange[0].trim() &&
        dateFilterRange[1].trim()
          ? `CAST(s.RegistrDate AS DATE) BETWEEN '${dateFilterRange[0]}' AND '${dateFilterRange[1]}'`
          : "";
      let whereClause = "";
      if (rangeCondition) {
        filters.push(rangeCondition);
      }
      if (filters.length > 0) {
        whereClause = `AND ${filters.join(" AND ")}`;
      } else {
        whereClause = ``;
      }
      const baseQuery = "SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode JOIN catmas ct ON ct.CatCode = s.catCode JOIN intakemas im ON im.IntakeCode = s.IntakeCode JOIN locmas lo ON lo.LocCode = s.LocCode"
      const baseQueryTwo = "SELECT COUNT(s.StdCode) FROM StudentMas s LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode JOIN catmas ct ON ct.CatCode = s.catCode JOIN intakemas im ON im.IntakeCode = s.IntakeCode JOIN locmas lo ON lo.LocCode = s.LocCode"
      const queries = {
        allStudents: `${baseQuery} WHERE s.StdCode IS NOT NULL ${whereClause}`,
        checkListNotAdded: `${baseQuery} WHERE sd.doccode IS NULL ${whereClause}`,
        checkListAdded: `${baseQuery} WHERE sd.doccode IS NOT NULL ${whereClause}`,
        totalDocAdded: `${baseQueryTwo} WHERE sd.doccode IS NOT NULL ${whereClause}`,
        uploadedDocuments: `${baseQueryTwo} WHERE sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL ${whereClause}`,
        notUploadedDocuments: `${baseQueryTwo} WHERE sd.doccode IS NOT NULL AND (sd.filename = '' OR sd.filename IS NULL) ${whereClause}`,
        approvedDocuments: `${baseQueryTwo} WHERE sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL AND sd.status = 'Approved' ${whereClause}`,
        rejectedDocuments: `${baseQueryTwo} WHERE sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL AND sd.status = 'Reject' ${whereClause}`,
        apprPendingDocuments: `${baseQueryTwo} WHERE sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL AND sd.status NOT IN ('Approved', 'Reject') ${whereClause}`,
        withInTat: `${baseQueryTwo} WHERE sd.filename IS NOT NULL AND sd.filename != '' AND sd.status = 'Approved' AND (CONVERT(DATE, sd.AppvDate) = CONVERT(DATE, sd.AddDate) OR CONVERT(DATE, sd.AppvDate) = DATEADD(DAY, 1, CONVERT(DATE, sd.AddDate))) ${whereClause} `,
        withOutTat: `${baseQueryTwo} WHERE sd.filename IS NOT NULL AND sd.filename != '' AND sd.status = 'Approved' AND CONVERT(DATE, sd.AppvDate) != CONVERT(DATE, sd.AddDate) AND CONVERT(DATE, sd.AppvDate) != DATEADD(DAY, 1, CONVERT(DATE, sd.AddDate)) ${whereClause}`,
      };
      const responseObj = {};
      for (const query in queries) {
        const result = await fetchBaseQuery(queries[query]);
        responseObj[query] = {
          count: result[0]?.Column1,
          query: queries[query],
        };
      }
      // console.log("setAllDocData", responseObj);
      setAllDocData(responseObj);
    } catch (error) {
      console.error(error);
      setAllReportLoading(false);
    } finally {
      setAllReportLoading(false);
    }
  };
  
  const fetchDocumentUploadReports = async () => {
    setDocRatioLoading(true);
    try {
      const filters = [
        buildCondition("s.intakeCode", filterOptions.intake, true),
        buildCondition("s.ststatus", filterOptions.ststatus, true),
        buildCondition("s.packageCode", filterOptions.package, true),
        buildCondition("s.LocCode", filterOptions.location, true),
        buildCondition("sd.DoctypeCode", filterOptions.checklist),
        buildCondition("s.coursetype", filterOptions.course),
        buildCondition("d.mandatory", filterOptions.mandatory),
        buildCondition("s.catCode", filterOptions.category),
      ].filter(Boolean); // remove empty conditions

      const rangeCondition =
        dateFilterRange?.length === 2 &&
        typeof dateFilterRange[0] === "string" &&
        typeof dateFilterRange[1] === "string" &&
        dateFilterRange[0].trim() &&
        dateFilterRange[1].trim()
          ? `CAST(s.RegistrDate AS DATE) BETWEEN '${dateFilterRange[0]}' AND '${dateFilterRange[1]}'`
          : "";

      let whereClause = "";
      if (rangeCondition) {
        filters.push(rangeCondition);
      }
      if (filters.length > 0) {
        whereClause = `AND ${filters.join(" AND ")}`;
      } else {
        whereClause = ``;
      }

      const queries = {
        totalDocuments: `
        SELECT s.StdCode, COUNT(sd.doccode) AS doccount 
        FROM StudentMas s 
        LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode 
        LEFT JOIN DocMas d ON d.doccode = sd.doccode 
        WHERE s.StdCode IS NOT NULL ${whereClause} 
        GROUP BY s.StdCode`,

        uploadedDocuments: `
        SELECT s.StdCode, COUNT(sd.doccode) AS doccount 
        FROM StudentMas s 
        LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode 
        LEFT JOIN DocMas d ON d.doccode = sd.doccode 
        WHERE sd.filename IS NOT NULL ${whereClause} 
        GROUP BY s.StdCode`,
      };

      const responseObj = {};
      for (const key in queries) {
        responseObj[key] = await fetchBaseQuery(queries[key]);
      }

      const totalDocuments = responseObj.totalDocuments || [];
      const uploadedDocuments = responseObj.uploadedDocuments || [];

      const studentDocsRatio = totalDocuments.map((totalDocItem) => {
        const uploadedDocItem = uploadedDocuments.find(
          (u) => u.STDCODE === totalDocItem.STDCODE
        );

        const totalDoc = totalDocItem.DOCCOUNT;
        const uploadedDoc = uploadedDocItem ? uploadedDocItem.DOCCOUNT : 0;
        const percentage =
          totalDoc === 0 ? 0 : +((uploadedDoc / totalDoc) * 100).toFixed(2);

        return {
          stdcode: totalDocItem.STDCODE,
          totalDoc,
          uploadedDoc,
          percentage,
        };
      });

      const docUploadSummary = {
        lessthan10: 0,
        lessthan30: 0,
        lessthan50: 0,
        lessthan75: 0,
        lessthan90: 0,
        full: 0,
      };

      studentDocsRatio.forEach(({ percentage }) => {
        if (percentage < 10) docUploadSummary.lessthan10++;
        else if (percentage < 30) docUploadSummary.lessthan30++;
        else if (percentage < 50) docUploadSummary.lessthan50++;
        else if (percentage < 75) docUploadSummary.lessthan75++;
        else if (percentage < 90) docUploadSummary.lessthan90++;
        else if (percentage >= 99.99) docUploadSummary.full++;
      });

      setDocRatioData(docUploadSummary);
      // Return if needed
      return { studentDocsRatio, docUploadSummary };
    } catch (error) {
      setDocRatioLoading(false);
      console.error("Error in fetchDocumentUploadReports:", error);
    } finally {
      setDocRatioLoading(false);
    }
  };

  const fetchOtherCordinatorDocumentsReports = async () => {
    try {
      const filters = [];
      const intakeCondition = buildCondition(
        "s.intakeCode",
        filterOptions.intake,
        true
      );
      const packageCondition = buildCondition(
        "s.packageCode",
        filterOptions.package,
        true
      );
      const ststatusCondition = buildCondition(
        "s.ststatus",
        filterOptions.ststatus,
        true
      );
      const locationCondition = buildCondition(
        "s.LocCode",
        filterOptions.location,
        true
      );
      const doctTypeCondition = buildCondition(
        "sd.DoctypeCode",
        filterOptions.checklist,
        true
      );

      const courseTypeCondition = buildCondition(
        "s.coursetype",
        filterOptions.course
      );
      const mandatoryTypeCondition = buildCondition(
        "d.mandatory",
        filterOptions.mandatory
      );
      const categoryTypeCondition = buildCondition(
        "s.catCode",
        filterOptions.category
      );

      [
        intakeCondition,
        ststatusCondition,
        packageCondition,
        locationCondition,
        doctTypeCondition,
        courseTypeCondition,
        mandatoryTypeCondition,
        categoryTypeCondition
      ].forEach((cond) => {
        if (cond) filters.push(cond);
      });
      const rangeCondition =
        dateFilterRange?.length === 2 &&
        typeof dateFilterRange[0] === "string" &&
        typeof dateFilterRange[1] === "string" &&
        dateFilterRange[0].trim() &&
        dateFilterRange[1].trim()
          ? `CAST(s.RegistrDate AS DATE) BETWEEN '${dateFilterRange[0]}' AND '${dateFilterRange[1]}'`
          : "";

      const allFilters = [...filters];
      if (rangeCondition) {
        allFilters.push(rangeCondition);
      }
      const whereClause = allFilters.length
        ? `AND  ${allFilters.join(" AND ")}`
        : ``;
      const baseQuery =
        "SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.Counsellorname NOT IN ('Ansh', 'Shashank', 'Riya', 'Sruthi', 'Drishti', 'Gaurav')";
      const baseQueryTwo =
        "SELECT COUNT(s.StdCode) FROM StudentMas s LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE Counsellorname NOT IN ('Ansh', 'Shashank', 'Riya', 'Sruthi', 'Drishti', 'Gaurav')";
      const queries = {
        allStudents: `${baseQuery} ${whereClause}`,
        checkListNotAdded: `${baseQuery} AND sd.doccode IS NULL ${whereClause}`,
        checkListAdded: `${baseQuery} AND sd.doccode IS NOT NULL ${whereClause}`,
        totalDocAdded: `${baseQueryTwo} AND sd.doccode IS NOT NULL ${whereClause}`,
        uploadedDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL ${whereClause}`,
        notUploadedDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND (sd.filename = '' OR sd.filename IS NULL) ${whereClause}`,
        approvedDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL AND sd.status = 'Approved' ${whereClause}`,
        rejectedDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL AND sd.status = 'Reject' ${whereClause}`,
        apprPendingDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL AND sd.status NOT IN ('Approved', 'Reject') ${whereClause}`,
        withInTat: `${baseQueryTwo} AND sd.filename IS NOT NULL AND sd.filename != '' AND sd.status = 'Approved' AND (CONVERT(DATE, sd.AppvDate) = CONVERT(DATE, sd.AddDate) OR CONVERT(DATE, sd.AppvDate) = DATEADD(DAY, 1, CONVERT(DATE, sd.AddDate))) ${whereClause} `,
        withOutTat: `${baseQueryTwo} AND sd.filename IS NOT NULL AND sd.filename != '' AND sd.status = 'Approved' AND CONVERT(DATE, sd.AppvDate) != CONVERT(DATE, sd.AddDate) AND CONVERT(DATE, sd.AppvDate) != DATEADD(DAY, 1, CONVERT(DATE, sd.AddDate)) ${whereClause}`,
      };
      const responseObj = {
        others: {
          details: {},
        },
      };
      for (const query in queries) {
        try {
          const result = await fetchBaseQuery(queries[query]);
          responseObj.others.details[query] = {
            query: queries[query],
            count: result[0]?.Column1 || null,
          };
        } catch (error) {
          console.error(`Error fetching data for ${query}:`, error);
          responseObj.others.details[query] = null;
        }
      }
      return responseObj;
    } catch (error) {
      console.log("error occured fetchOtherCordinatorDocumentsReports", error);
    }
  };

  const fetchDocumentsReportsCoordinatorWise = async () => {
    setCordinateLoading(true);
    try {
      const coordinatorList = await fetchBaseQuery(
        `SELECT DISTINCT Counsellorname AS COUNSELOR 
       FROM StudentMas 
       WHERE Counsellorname != '' 
         AND Counsellorname IN ('Ansh', 'Shashank', 'Riya', 'Sruthi', 'Drishti', 'Gaurav')`
      );

      if (!Array.isArray(coordinatorList) || coordinatorList.length === 0) {
        console.log("No records to execute");
        setCordinateLoading(false);
        return [];
      }

      setCordinatorList(coordinatorList);

      // Build all filters
      const filters = [];
      const conditions = [
        buildCondition("s.intakeCode", filterOptions.intake),
        buildCondition("s.packageCode", filterOptions.package),
        buildCondition("s.ststatus", filterOptions.ststatus),
        buildCondition("s.LocCode", filterOptions.location),
        buildCondition("sd.DoctypeCode", filterOptions.checklist),
        buildCondition("s.coursetype", filterOptions.course),
        buildCondition("d.mandatory", filterOptions.mandatory),
        buildCondition("s.catCode", filterOptions.category),
      ];

      conditions.forEach((cond) => cond && filters.push(cond));

      const rangeCondition =
        dateFilterRange?.length === 2 &&
        typeof dateFilterRange[0] === "string" &&
        typeof dateFilterRange[1] === "string" &&
        dateFilterRange[0].trim() &&
        dateFilterRange[1].trim()
          ? `CAST(s.RegistrDate AS DATE) BETWEEN '${dateFilterRange[0]}' AND '${dateFilterRange[1]}'`
          : "";

      const allFilters = [...filters];
      if (rangeCondition) {
        allFilters.push(rangeCondition);
      }
      const whereClause = allFilters.length
        ? `AND  ${allFilters.join(" AND ")}`
        : ``;

      const baseQuery =
        "SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.StdCode IS NOT NULL";
      const baseQueryTwo =
        "SELECT COUNT(s.StdCode) FROM StudentMas s LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.StdCode IS NOT NULL";
      const queries = {
        allStudents: `${baseQuery} ${whereClause}`,
        checkListNotAdded: `${baseQuery} AND sd.doccode IS NULL ${whereClause}`,
        checkListAdded: `${baseQuery} AND sd.doccode IS NOT NULL ${whereClause}`,
        totalDocAdded: `${baseQueryTwo} AND sd.doccode IS NOT NULL ${whereClause}`,
        uploadedDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL ${whereClause}`,
        notUploadedDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND (sd.filename = '' OR sd.filename IS NULL) ${whereClause}`,
        approvedDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL AND sd.status = 'Approved' ${whereClause}`,
        rejectedDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL AND sd.status = 'Reject' ${whereClause}`,
        apprPendingDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL AND sd.status NOT IN ('Approved', 'Reject') ${whereClause}`,
        withInTat: `${baseQueryTwo} AND sd.filename IS NOT NULL AND sd.filename != '' AND sd.status = 'Approved' AND (CONVERT(DATE, sd.AppvDate) = CONVERT(DATE, sd.AddDate) OR CONVERT(DATE, sd.AppvDate) = DATEADD(DAY, 1, CONVERT(DATE, sd.AddDate))) ${whereClause} `,
        withOutTat: `${baseQueryTwo} AND sd.filename IS NOT NULL AND sd.filename != '' AND sd.status = 'Approved' AND CONVERT(DATE, sd.AppvDate) != CONVERT(DATE, sd.AddDate) AND CONVERT(DATE, sd.AppvDate) != DATEADD(DAY, 1, CONVERT(DATE, sd.AddDate)) ${whereClause}`,
      };

      const responseObj = [];

      for (const item of coordinatorList) {
        const coordinator = item.COUNSELOR;
        const coordinatorData = { coordinator, details: {} };

        for (const [key, baseQuery] of Object.entries(queries)) {
          // Inject counsellor condition
          const finalQuery = `${baseQuery} AND s.Counsellorname = '${coordinator}'`;

          try {
            const result = await fetchBaseQuery(finalQuery);
            coordinatorData.details[key] = {
              query: finalQuery,
              count: result[0]?.Column1 || 0,
            };
          } catch (error) {
            console.error(`Error fetching ${key} for ${coordinator}:`, error);
            coordinatorData.details[key] = null;
          }
        }

        responseObj.push(coordinatorData);
      }

      // Handle "other" coordinators
      const otherData = await fetchOtherCordinatorDocumentsReports();
      const otherDetails = otherData?.others?.details || {};
      responseObj.push({ coordinator: "other", details: otherDetails });

      setCoordinatorData(responseObj);
    } catch (error) {
      console.error("Failed to fetch coordinator reports", error);
    } finally {
      setCordinateLoading(false);
    }
  };

  const fetchDocumentUploadReportsCordinatorWise = async () => {
    setCordinatorDocLoading(true);

    try {
      const baseFilters = [
        buildCondition("s.intakeCode", filterOptions.intake, true),
        buildCondition("s.packageCode", filterOptions.package, true),
        buildCondition("s.LocCode", filterOptions.location, true),
        buildCondition("s.ststatus", filterOptions.ststatus, true),
        buildCondition("sd.DoctypeCode", filterOptions.checklist),
        buildCondition("s.coursetype", filterOptions.course),
        buildCondition("d.mandatory", filterOptions.mandatory),
        buildCondition("s.catCode", filterOptions.category),
      ].filter(Boolean);

      const cordinatorList = await fetchBaseQuery(`
      SELECT DISTINCT Counsellorname AS COUNSELOR
      FROM StudentMas
      WHERE Counsellorname != ''
        AND Counsellorname IN ('Ansh', 'Shashank', 'Riya', 'Sruthi', 'Drishti', 'Gaurav')
    `);

      const summaryReport = [];

      const rangeCondition =
        dateFilterRange?.length === 2 &&
        typeof dateFilterRange[0] === "string" &&
        typeof dateFilterRange[1] === "string" &&
        dateFilterRange[0].trim() &&
        dateFilterRange[1].trim()
          ? `AND CAST(s.RegistrDate AS DATE) BETWEEN '${dateFilterRange[0]}' AND '${dateFilterRange[1]}'`
          : "";

      for (const { COUNSELOR } of cordinatorList) {
        const filters = [...baseFilters, `s.Counsellorname = '${COUNSELOR}'`];
        const whereClause = filters.length
          ? `AND ${filters.join(" AND ")}`
          : "";

        const queries = {
          totalDocuments: `
          SELECT s.StdCode, COUNT(sd.doccode) AS doccount 
          FROM StudentMas s 
          LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode 
          LEFT JOIN DocMas d ON d.doccode = sd.doccode 
          WHERE s.StdCode IS NOT NULL ${whereClause} ${rangeCondition}
          GROUP BY s.StdCode`,

          uploadedDocuments: `
          SELECT s.StdCode, COUNT(sd.doccode) AS doccount 
          FROM StudentMas s 
          LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode 
          JOIN DocMas d ON d.doccode = sd.doccode 
          WHERE sd.filename IS NOT NULL ${whereClause} ${rangeCondition}
          GROUP BY s.StdCode`,
        };

        const responseObj = {};
        for (const key in queries) {
          responseObj[key] = await fetchBaseQuery(queries[key]);
        }

        const totalDocuments = Array.isArray(responseObj.totalDocuments)
          ? responseObj.totalDocuments
          : [];
        const uploadedDocuments = Array.isArray(responseObj.uploadedDocuments)
          ? responseObj.uploadedDocuments
          : [];

        if (!totalDocuments.length && !uploadedDocuments.length) {
          setCordinatorDocData([]);
          return [];
        }

        const studentDocsRatio = totalDocuments.map((totalDocItem) => {
          const uploadedDocItem = uploadedDocuments.find(
            (u) => u.STDCODE === totalDocItem.STDCODE
          );

          const totalDoc = Number(totalDocItem.DOCCOUNT);
          const uploadedDoc = uploadedDocItem
            ? Number(uploadedDocItem.DOCCOUNT)
            : 0;
          const percentage =
            totalDoc === 0 ? 0 : +((uploadedDoc / totalDoc) * 100).toFixed(2);

          return {
            stdcode: totalDocItem.STDCODE,
            totalDoc,
            uploadedDoc,
            percentage,
          };
        });

        const docUploadSummary = {
          coordinator: COUNSELOR,
          counts: {
            zero: 0,
            lessthan10: 0,
            lessthan30: 0,
            lessthan50: 0,
            lessthan75: 0,
            lessthan90: 0,
            full: 0,
          },
        };

        studentDocsRatio.forEach(({ percentage }) => {
          if (percentage === 0) docUploadSummary.counts.zero++;
          else if (percentage < 10) docUploadSummary.counts.lessthan10++;
          else if (percentage < 30) docUploadSummary.counts.lessthan30++;
          else if (percentage < 50) docUploadSummary.counts.lessthan50++;
          else if (percentage < 75) docUploadSummary.counts.lessthan75++;
          else if (percentage < 90) docUploadSummary.counts.lessthan90++;
          else if (percentage >= 99.99) docUploadSummary.counts.full++;
        });

        summaryReport.push(docUploadSummary);
      }
      setCordinatorDocData(summaryReport);
      return summaryReport;
    } catch (error) {
      console.error(
        "Error in fetchDocumentUploadReportsCordinatorWise:",
        error
      );
    } finally {
      setCordinatorDocLoading(false);
    }
  };

  const fetchDocumentsReportIntakeWise = async () => {
    setIntakeDataLoading(true);
    try {
      // Build all filters
      const filters = [];
      const intakeCondition = buildCondition(
        "s.intakeCode",
        filterOptions.intake,
        true
      );
      const conditions = [
        buildCondition("s.intakeCode", filterOptions.intake, true),
        buildCondition("s.packageCode", filterOptions.package, true),
        buildCondition("s.ststatus", filterOptions.ststatus, true),
        buildCondition("s.LocCode", filterOptions.location, true),
        buildCondition("sd.DoctypeCode", filterOptions.checklist),
        buildCondition("s.coursetype", filterOptions.course),
        buildCondition("d.mandatory", filterOptions.mandatory),
        buildCondition("s.catCode", filterOptions.category),
      ];

      conditions.forEach((cond) => cond && filters.push(cond));

      // Handle date range
      const rangeCondition =
        dateFilterRange?.length === 2 &&
        typeof dateFilterRange[0] === "string" &&
        typeof dateFilterRange[1] === "string" &&
        dateFilterRange[0].trim() &&
        dateFilterRange[1].trim()
          ? `CAST(s.RegistrDate AS DATE) BETWEEN '${dateFilterRange[0]}' AND '${dateFilterRange[1]}'`
          : "";

      if (rangeCondition) filters.push(rangeCondition);

      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : "";

      const baseQuery =
        "SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.StdCode IS NOT NULL";
      const baseQueryTwo =
        "SELECT COUNT(s.StdCode) FROM StudentMas s LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.StdCode IS NOT NULL";
      const queries = {
        allStudents: `${baseQuery} ${whereClause}`,
        checkListNotAdded: `${baseQuery} AND sd.doccode IS NULL ${whereClause}`,
        checkListAdded: `${baseQuery} AND sd.doccode IS NOT NULL ${whereClause}`,
        totalDocAdded: `${baseQueryTwo} AND sd.doccode IS NOT NULL ${whereClause}`,
        uploadedDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL ${whereClause}`,
        notUploadedDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND (sd.filename = '' OR sd.filename IS NULL) ${whereClause}`,
        approvedDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL AND sd.status = 'Approved' ${whereClause}`,
        rejectedDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL AND sd.status = 'Reject' ${whereClause}`,
        apprPendingDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL AND sd.status NOT IN ('Approved', 'Reject') ${whereClause}`,
        withInTat: `${baseQueryTwo} AND sd.filename IS NOT NULL AND sd.filename != '' AND sd.status = 'Approved' AND (CONVERT(DATE, sd.AppvDate) = CONVERT(DATE, sd.AddDate) OR CONVERT(DATE, sd.AppvDate) = DATEADD(DAY, 1, CONVERT(DATE, sd.AddDate))) ${whereClause} `,
        withOutTat: `${baseQueryTwo} AND sd.filename IS NOT NULL AND sd.filename != '' AND sd.status = 'Approved' AND CONVERT(DATE, sd.AppvDate) != CONVERT(DATE, sd.AddDate) AND CONVERT(DATE, sd.AppvDate) != DATEADD(DAY, 1, CONVERT(DATE, sd.AddDate)) ${whereClause}`,
      };
      const intakeQuery = `SELECT s.IntakeCode AS Code, s.Intakename AS Name  FROM intakemas s WHERE 1=1 ${
        intakeCondition.length ? `AND ${intakeCondition}` : ""
      }`;
      const intakeList = await fetchBaseQuery(intakeQuery);
      if (!Array.isArray(intakeList) || intakeList.length === 0) {
        console.log("No records to execute");
        return [];
      }
      const responseObj = [];

      if (filterOptions.intake) {
      }

      for (const item of intakeList) {
        const intakeCode = item.CODE;
        const intakeData = { intake: item.NAME, details: {} };

        for (const [key, baseQuery] of Object.entries(queries)) {
          // Inject counsellor condition
          const finalQuery = `${baseQuery} AND s.intakeCode = ${intakeCode}`;

          try {
            const result = await fetchBaseQuery(finalQuery);
            intakeData.details[key] = result
              ? {
                  query: finalQuery,
                  count: result[0]?.Column1 || 0,
                }
              : {
                  query: finalQuery,
                  count: 0,
                };
          } catch (error) {
            console.error(`Error fetching ${key} for ${intakeCode}:`, error);
            intakeData.details[key] = null;
          }
        }

        responseObj.push(intakeData);
      }
      setIntakeData(responseObj);
    } catch (error) {
      console.error("Failed to fetch coordinator reports", error);
      setIntakeDataLoading(false);
    } finally {
      setIntakeDataLoading(false);
    }
  };

  const fetchDocumentsReportMonthWise = async () => {
    setMonthDataLoading(true);
    try {
      const filters = [];
      const intakeCondition = buildCondition(
        "s.intakeCode",
        filterOptions.intake,
        true
      );
      const packageCondition = buildCondition(
        "s.packageCode",
        filterOptions.package,
        true
      );
      const locationCondition = buildCondition(
        "s.LocCode",
        filterOptions.location,
        true
      );
      const ststatusCondition = buildCondition(
        "s.ststatus",
        filterOptions.ststatus,
        true
      );
      const doctTypeCondition = buildCondition(
        "sd.DoctypeCode",
        filterOptions.checklist,
        true
      );
      const courseTypeCondition = buildCondition(
        "s.coursetype",
        filterOptions.course
      );

      const mandatoryTypeCondition = buildCondition(
        "d.mandatory",
        filterOptions.mandatory
      );

      const categoryTypeCondition = buildCondition(
        "s.catCode",
        filterOptions.category
      );

      [
        intakeCondition,
        ststatusCondition,
        packageCondition,
        locationCondition,
        doctTypeCondition,
        courseTypeCondition,
        mandatoryTypeCondition,
        categoryTypeCondition
      ].forEach((cond) => {
        if (cond) filters.push(cond);
      });

      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : ``;

      const baseQuery =
        "SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.StdCode IS NOT NULL";
      const baseQueryTwo =
        "SELECT COUNT(s.StdCode) FROM StudentMas s LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.StdCode IS NOT NULL";
      const queries = {
        allStudents: `${baseQuery} ${whereClause}`,
        checkListNotAdded: `${baseQuery} AND sd.doccode IS NULL ${whereClause}`,
        checkListAdded: `${baseQuery} AND sd.doccode IS NOT NULL ${whereClause}`,
        totalDocAdded: `${baseQueryTwo} AND sd.doccode IS NOT NULL ${whereClause}`,
        uploadedDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL ${whereClause}`,
        notUploadedDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND (sd.filename = '' OR sd.filename IS NULL) ${whereClause}`,
        approvedDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL AND sd.status = 'Approved' ${whereClause}`,
        rejectedDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL AND sd.status = 'Reject' ${whereClause}`,
        apprPendingDocuments: `${baseQueryTwo} AND sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL AND sd.status NOT IN ('Approved', 'Reject') ${whereClause}`,
        withInTat: `${baseQueryTwo} AND sd.filename IS NOT NULL AND sd.filename != '' AND sd.status = 'Approved' AND (CONVERT(DATE, sd.AppvDate) = CONVERT(DATE, sd.AddDate) OR CONVERT(DATE, sd.AppvDate) = DATEADD(DAY, 1, CONVERT(DATE, sd.AddDate))) ${whereClause} `,
        withOutTat: `${baseQueryTwo} AND sd.filename IS NOT NULL AND sd.filename != '' AND sd.status = 'Approved' AND CONVERT(DATE, sd.AppvDate) != CONVERT(DATE, sd.AddDate) AND CONVERT(DATE, sd.AppvDate) != DATEADD(DAY, 1, CONVERT(DATE, sd.AddDate)) ${whereClause}`,
      };
      if (rangeList && rangeList.length > 0) {
        setDateRange(rangeList);
      }
      const responseObj = [];
      for (const item of rangeList) {
        const monthName = dayjs(item.start).format("MMMM YYYY");
        const months = { month: monthName, details: {} };

        for (const query in queries) {
          try {
            const dynamicQuery = `${queries[query]} AND s.RegistrDate IS NOT NULL AND s.RegistrDate != '' AND CAST(s.RegistrDate AS DATE) BETWEEN '${item.start}' AND '${item.end}'`;

            const result = await fetchBaseQuery(dynamicQuery);
            months.details[query] = {
              query: dynamicQuery,
              count: result[0]?.Column1 || null,
            };
          } catch (error) {
            console.error(`Error fetching data for ${query}:`, error);
            months.details[query] = null;
          }
        }

        responseObj.push(months);
      }

      setMonthData(responseObj);
      // console.log("first", responseObj)
    } catch (error) {
      console.log(error)
      setMonthDataLoading(false);
    } finally {
      setMonthDataLoading(false);
    }
  };

  const fetchDocWiseReports = async () => {
    setDocLoading(true);
    try {
      const docList = await fetchBaseQuery(
        "SELECT doccode, docname FROM DocMas WHERE doccode IN (1, 81, 212)"
      );

      if (!Array.isArray(docList) || docList.length === 0) {
        console.log("No records to execute");
        return [];
      }

      const filters = [];
      const intakeCondition = buildCondition(
        "s.intakeCode",
        filterOptions.intake,
        true
      );
      const packageCondition = buildCondition(
        "s.packageCode",
        filterOptions.package,
        true
      );
      const locationCondition = buildCondition(
        "s.LocCode",
        filterOptions.location,
        true
      );
      const doctTypeCondition = buildCondition(
        "sd.DoctypeCode",
        filterOptions.checklist,
        true
      );
      const courseTypeCondition = buildCondition(
        "s.coursetype",
        filterOptions.course
      );
      const mandatoryTypeCondition = buildCondition(
        "d.mandatory",
        filterOptions.mandatory
      );

      const categoryTypeCondition = buildCondition(
        "s.catCode",
        filterOptions.category
      );

      [
        intakeCondition,
        packageCondition,
        locationCondition,
        doctTypeCondition,
        courseTypeCondition,
        mandatoryTypeCondition,
        categoryTypeCondition
      ].forEach((cond) => {
        if (cond) filters.push(cond);
      });

      // Handle date range
      const rangeCondition =
        dateFilterRange?.length === 2 &&
        typeof dateFilterRange[0] === "string" &&
        typeof dateFilterRange[1] === "string" &&
        dateFilterRange[0].trim() &&
        dateFilterRange[1].trim()
          ? `CAST(s.RegistrDate AS DATE) BETWEEN '${dateFilterRange[0]}' AND '${dateFilterRange[1]}'`
          : "";

      const allFilters = [...filters];
      if (rangeCondition) {
        allFilters.push(rangeCondition);
      }
      const whereClause = allFilters.length
        ? `AND  ${allFilters.join(" AND ")}`
        : ``;
      const baseQuery =
        "SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.StdCode IS NOT NULL";
      const baseQueryTwo =
        "SELECT COUNT(s.StdCode) FROM StudentMas s LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode WHERE s.StdCode IS NOT NULL";

      const queries = {
        totalStudents: `${baseQuery} ${whereClause}`,
        docUploaded: `${baseQuery} AND sd.filename IS NOT NULL AND sd.filename != '' ${whereClause} `,
        docNotUploaded: `${baseQuery} ${whereClause} AND (sd.filename IS NULL OR sd.filename = '')`,
        docApproved: `${baseQuery} ${whereClause} AND sd.filename IS NOT NULL AND sd.filename != '' AND sd.status = 'Approved'`,
        docRejected: `${baseQuery} ${whereClause} AND sd.filename IS NOT NULL AND sd.filename != '' AND sd.status = 'Reject'`,
        apprPendingDocuments: `${baseQuery} AND sd.doccode IS NOT NULL AND sd.filename != '' AND sd.filename IS NOT NULL AND sd.status NOT IN ('Approved', 'Reject') ${whereClause}`,
      };

      const responseObj = [];
      for (const item of docList) {
        const docData = { docname: item.DOCNAME, details: {} };

        for (const query in queries) {
          try {
            const result = await fetchBaseQuery(
              `${queries[query]} AND d.doccode = '${item.DOCCODE}'`
            );
            docData.details[query] = result
              ? {
                  query: `${queries[query]} AND d.doccode = '${item.DOCCODE}'`,
                  count: result[0]?.Column1,
                }
              : {
                  query: `${queries[query]} AND d.doccode = '${item.DOCCODE}'`,
                  count: null,
                };
          } catch (error) {
            console.error(`Error fetching data for ${query}:`, error);
            docData.details[query] = null;
          }
        }

        responseObj.push(docData);
      }
      setDocWiseData(responseObj);
    } catch (error) {
      console.log("error occurred in fetchDocWiseReports");
      setDocLoading(false);
    } finally {
      setDocLoading(false);
    }
  };

  const checkAuthentication = async () => {
    try {
      if (userCode === 0) {
        setAuthState(true);
        return true;
      }

      const checkAdmin = await fetchBaseQuery(
        `SELECT * FROM usermas WHERE UserCode = ${userCode} AND deptcode IN (15, 17, 16, 4, 12)`
      );

      if (Array.isArray(checkAdmin) && checkAdmin.length > 0) {
        setAuthState(true);
        return true;
      }

      setAuthState(false);
      return false;
    } catch (error) {
      console.error("Error checking authentication:", error);
      setAuthState(false);
      return false;
    }
  };

  useEffect(() => {
    checkAuthentication();
  }, [userCode]);

  useEffect(() => {
    fetchDocumentsReportMonthWise();
    fetchFilterDropdown();
    fetchAllDocumentsReports();
    fetchDocumentUploadReports();
    fetchDocumentsReportsCoordinatorWise();
    fetchDocumentUploadReportsCordinatorWise();
    fetchDocumentsReportIntakeWise();
   
    fetchDocWiseReports();
  }, []);

  // useEffect(() => {
  //   fetchDocumentsReportMonthWise();
  // }, [yearState, counselorName]);

  const handleFilterChange = () => {
    fetchFilterDropdown();
    fetchAllDocumentsReports();
    fetchDocumentsReportsCoordinatorWise();
    fetchDocumentUploadReportsCordinatorWise();
    fetchDocumentsReportIntakeWise();
    fetchDocumentsReportMonthWise();
    fetchDocumentUploadReports();
    fetchDocWiseReports();
  };
  // const clearAllFilters = () => {
  //   setFilterOptions({
  //     location: [],
  //     package: [],
  //     intake: [],
  //     checklist: [],
  //     course: [],
  //     mandatory: [],
  //     ststatus: [],
  //     category: [],
  //   });
  //   fetchFilterDropdown();
  //   fetchDocumentUploadReports();
  //   fetchAllDocumentsReports();
  //   fetchDocumentsReportsCoordinatorWise();
  //   fetchDocWiseReports();
  // };

  return authState ? (
    <div className="transback h-100 overflow-auto p-3 d-flex flex-column">
      <button
        className="btn text-secondary rounded-pill px-3 py-1 d-flex d-lg-none align-items-center gap-2"
        onClick={() => setShowSideOverlay(true)}
      >
        <MenuIcon />
      </button>
      <div className="headerUserDetails py-1 mb-3 d-flex justify-content-between">
        <span>Document Dashboard</span>
        <div
          className="d-flex gap-2 align-items-center"
          title={`Name: ${user?.name} | Email: ${user?.email}`}
        >
          <i className="fa fa-user"></i>
          <span>{user?.name}</span>
        </div>
      </div>

      <div className="w-100 h-100 overflow-auto ">
        {spinnerLoading ? (
          <div className="w-100 h-100 d-flex align-items-center justify-content-center">
            <CustomSpinner size={1} />
          </div>
        ) : (
          <>
            <div className="container-fluid">
              <div className="header py-3 border border-light px-2 rounded bg-light">
                <h5>Filter</h5>
                <div className="row g-2 g-lg-3 mb-3">
                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="intake-select"
                        options={filterDropdown.intakeOptions}
                        isMulti
                        isClearable
                        placeholder="Select Intake"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            intake: selectedOptions
                              ? selectedOptions.map((opt) =>
                                  parseInt(opt.value)
                                )
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="package-select"
                        options={filterDropdown.locationOptions}
                        isMulti
                        isClearable
                        placeholder="Select Branch"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            location: selectedOptions
                              ? selectedOptions.map((opt) =>
                                  parseInt(opt.value)
                                )
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="ststatus-select"
                        options={stStatusOptions}
                        isMulti
                        isClearable
                        placeholder="Select Student Status"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            ststatus: selectedOptions
                              ? selectedOptions.map((opt) =>
                                  parseInt(opt.value)
                                )
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="category-select"
                        options={filterDropdown.categoryOptions}
                        isMulti
                        isClearable
                        placeholder="Select Student Category"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            category: selectedOptions
                              ? selectedOptions.map((opt) =>
                                  parseInt(opt.value)
                                )
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="package-select"
                        options={packageCodes}
                        isMulti
                        isClearable
                        placeholder="Select Package"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            package: selectedOptions
                              ? selectedOptions.map((opt) =>
                                  parseInt(opt.value)
                                )
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="course-select"
                        options={[
                          { label: "Master's", value: "M" },
                          { label: "Bachelor's", value: "B" },
                        ]}
                        isMulti
                        isClearable
                        placeholder="Select Course"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            course: selectedOptions
                              ? selectedOptions.map((opt) => opt.value)
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="checklist-select"
                        options={filterDropdown.checklistOptions}
                        isMulti
                        isClearable
                        placeholder="Select Checklist"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            checklist: selectedOptions
                              ? selectedOptions.map((opt) =>
                                  parseInt(opt.value)
                                )
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="mandatory-select"
                        options={[
                          { label: "Yes", value: "1" },
                          { label: "No", value: "0" },
                        ]}
                        isMulti
                        isClearable
                        placeholder="Select Mandatory"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            mandatory: selectedOptions
                              ? selectedOptions.map((opt) => opt.value)
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-1 text-end">
                    <label htmlFor="">Select Range</label>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group d-flex">
                      <input
                        type="date"
                        className="form-control"
                        // value={dateFilterRange[0] || ""}
                        onChange={(e) => handleChange(0, e.target.value)}
                      />
                      <input
                        type="date"
                        className="form-control"
                        // value={dateFilterRange[1] || ""}
                        onChange={(e) => handleChange(1, e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="form-group col-md-2">
                    <button
                      type="button"
                      className="btn btn-warning btn-sm"
                      onClick={handleFilterChange}
                    >
                      Apply Filter
                    </button>
                  </div>
                </div>
              </div>
              <div className="w-100">
                <DocumentTables
                  allDocumentData={allDocData}
                  allReportLoading={allReportLoading}
                  coordinatorData={coordinatorData}
                  cordinateLoading={cordinateLoading}
                  docWiseData={docWiseData}
                  docLoading={docLoading}
                  docRatioData={docRatioData}
                  docRatioLoading={docRatioLoading}
                  cordinatorDocLoading={cordinatorDocLoading}
                  cordinatorDocData={cordinatorDocData}
                  monthData={monthData}
                  monthDataLoading={monthDataLoading}
                  setYearState={setYearState}
                  setCounseleorName={setCounseleorName}
                  cordinatorList={cordinatorList}
                  intakeDataLoading={intakeDataLoading}
                  intakeData={intakeData}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  ) : (
    <NotAuthorize />
  );
};

export default DocumentDashboard;
